import { useCallback, useEffect } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { createAvatarSessionState } from '../../session';
import { replayAvatarRuntimeConfig } from '../avatarRuntimeConfig';
import type {
  AvatarSessionContext,
  AvatarSessionState,
  AvatarSessionStatus,
  AvatarTransportKind
} from '../../session';

export interface HostedLiveControllerEffectsRefs {
  liveAutoListenTimerRef: MutableRefObject<number | null>;
  liveSessionContextRef: MutableRefObject<AvatarSessionContext | null>;
  liveVoiceRetryTimerRef: MutableRefObject<number | null>;
}

export interface UseHostedLiveControllerEffectsOptions {
  refs: HostedLiveControllerEffectsRefs;
  /**
   * Instance id of the avatar this controller drives. Threaded into the
   * `linkedAvatarConfig` replay so the dispatched `avatar:config:update`
   * targets exactly that avatar instead of every mounted instance (and
   * so the replay dedup is keyed per instance rather than the shared
   * `__default__` slot, which starved remounts). Optional — the
   * standalone runtime-host shell (`App.tsx`) has a single id-less
   * avatar and passes nothing.
   */
  avatarInstanceId?: string;
  linkedAvatarConfig: Record<string, unknown> | null;
  liveConnectionError: string | null;
  liveModeActive: boolean;
  liveSessionState: AvatarSessionState | null;
  liveTransport: AvatarTransportKind;
  resolvedTtsBaseUrl?: string;
  setDisplayText: (value: string) => void;
  setLiveSessionState: Dispatch<SetStateAction<AvatarSessionState | null>>;
}

export interface UseHostedLiveControllerEffectsReturn {
  clearLiveAutoListenTimer: () => void;
  clearLiveVoiceRetryTimer: () => void;
  dispatchAvatarEvent: (eventName: string, detail: unknown) => void;
  setLiveStatus: (
    status: AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => void;
}

function clearTimer(timerRef: MutableRefObject<number | null>): void {
  if (timerRef.current === null) {
    return;
  }

  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

export function useHostedLiveControllerEffects({
  refs,
  avatarInstanceId,
  linkedAvatarConfig,
  liveConnectionError,
  liveModeActive,
  liveSessionState,
  liveTransport,
  resolvedTtsBaseUrl,
  setDisplayText,
  setLiveSessionState
}: UseHostedLiveControllerEffectsOptions): UseHostedLiveControllerEffectsReturn {
  const {
    liveAutoListenTimerRef,
    liveSessionContextRef,
    liveVoiceRetryTimerRef
  } = refs;

  const dispatchAvatarEvent = useCallback((eventName: string, detail: unknown) => {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }, []);

  const setLiveStatus = useCallback((
    status: AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => {
    const nextContext = context ?? liveSessionContextRef.current ?? {};
    setLiveSessionState(createAvatarSessionState(nextContext, {
      mode: 'live',
      transport: liveTransport,
      status,
      lastError
    }));
  }, [liveSessionContextRef, liveTransport, setLiveSessionState]);

  const clearLiveVoiceRetryTimer = useCallback(() => {
    clearTimer(liveVoiceRetryTimerRef);
  }, [liveVoiceRetryTimerRef]);

  const clearLiveAutoListenTimer = useCallback(() => {
    clearTimer(liveAutoListenTimerRef);
  }, [liveAutoListenTimerRef]);

  useEffect(() => {
    if (!resolvedTtsBaseUrl) {
      return;
    }

    window.__CAMLIN_TTS_URL__ = resolvedTtsBaseUrl;
  }, [resolvedTtsBaseUrl]);

  useEffect(() => {
    if (!liveModeActive || !liveSessionState) {
      delete (window as any).__CAMLIN_AVATAR_LIVE_SESSION__;
      return;
    }

    (window as any).__CAMLIN_AVATAR_LIVE_SESSION__ = liveSessionState;
    dispatchAvatarEvent('avatar:live:state', liveSessionState);
  }, [dispatchAvatarEvent, liveModeActive, liveSessionState]);

  useEffect(() => {
    if (!liveModeActive || !linkedAvatarConfig) {
      return;
    }

    replayAvatarRuntimeConfig(linkedAvatarConfig, { avatarInstanceId });
  }, [avatarInstanceId, linkedAvatarConfig, liveModeActive]);

  useEffect(() => {
    if (liveConnectionError) {
      setDisplayText(liveConnectionError);
    }
  }, [liveConnectionError, setDisplayText]);

  return {
    clearLiveAutoListenTimer,
    clearLiveVoiceRetryTimer,
    dispatchAvatarEvent,
    setLiveStatus
  };
}

export default useHostedLiveControllerEffects;
